import apiClient from './client';
import type { RebalancingDominantState } from '../lib/rebalancingCompletionProof';
import type { SourceProofMetadata } from '../types/sourceProof';

export interface RebalanceTarget {
  symbol: string;
  targetPct: number;
  tolerancePct: number;
}

export interface RebalanceAssetDrift {
  symbol: string;
  actualPct: number;
  targetPct: number;
  tolerancePct: number;
  /** actualPct - targetPct */
  driftPct: number;
  driftStatus: 'within' | 'over' | 'under';
  currentValueUsd: number;
  targetValueUsd: number;
  /** positive = buy, negative = sell */
  suggestedTradeUsd: number;
}

export interface RebalanceAdvisorData {
  userId: string;
  totalPortfolioUsd: number;
  targets: RebalanceTarget[];
  drifts: RebalanceAssetDrift[];
  hasAnyDrift: boolean;
  disclaimer: string;
  proof?: {
    dominantState: RebalancingDominantState;
    targetUpdatedAt: string | null;
    remainingOutOfToleranceCount: number;
    topPriorityAsset: RebalanceAssetDrift | null;
    topSuggestedTrade:
      | {
          action: 'BUY' | 'SELL';
          symbol: string;
          amountUsd: number;
          driftPct: number;
        }
      | null;
    holdingsSourceProof?: SourceProofMetadata;
  };
}

export async function getRebalanceAdvisor(userId: string): Promise<RebalanceAdvisorData> {
  const res = await apiClient.get<RebalanceAdvisorData>(`/analytics/portfolio/rebalance/${userId}`);
  return res.data;
}

export async function getRebalanceTargets(
  userId: string,
): Promise<{ userId: string; targets: RebalanceTarget[] }> {
  const res = await apiClient.get<{ userId: string; targets: RebalanceTarget[] }>(
    `/analytics/portfolio/rebalance/${userId}/targets`,
  );
  return res.data;
}

export async function saveRebalanceTargets(
  userId: string,
  targets: Array<{ symbol: string; targetPct: number; tolerancePct?: number }>,
): Promise<{ success: boolean; targets: RebalanceTarget[]; totalPct: number }> {
  const res = await apiClient.put<{ success: boolean; targets: RebalanceTarget[]; totalPct: number }>(
    `/analytics/portfolio/rebalance/${userId}/targets`,
    { targets },
  );
  return res.data;
}
